import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  Edit2,
  Trash2,
  Share2,
  ImageIcon,
} from "lucide-react";
import { DD, POST_TYPES, STATUS_PILLS } from "./constants";
import { computeKwScore, getPostStatusPill, formatPostDate } from "./utils";
import { getPostTags } from "./types";
import type { Post, Keyword } from "@shared/schema";

export function PostCard({
  post,
  keywords,
  onEdit,
  onPreview,
  onPublish,
  onSchedule,
  onDelete,
  onGenerateImage,
  isFocused,
}: {
  post: Post;
  keywords: Keyword[];
  onEdit: (post: Post) => void;
  onPreview: (post: Post) => void;
  onPublish: (post: Post) => void;
  onSchedule: (post: Post) => void;
  onDelete: (post: Post) => void;
  onGenerateImage: (post: Post) => void;
  isFocused?: boolean;
}) {
  const pt = POST_TYPES.find(t => t.value === post.postType) || POST_TYPES[0];
  const statusPillKey = getPostStatusPill(post);
  const statusPill = STATUS_PILLS[statusPillKey];
  const kwScore = computeKwScore(post.content, keywords);
  const dateDisplay = formatPostDate(post);

  return (
    <div
      className={`rounded-xl overflow-hidden transition-all cursor-pointer group flex flex-col ${isFocused ? "ring-2 ring-orange-500/50" : ""}`}
      style={{
        background: DD.surface,
        border: `1px solid ${DD.border}`,
      }}
      onClick={() => onEdit(post)}
      tabIndex={0}
      data-testid={`post-card-${post.id}`}
      data-post-id={post.id}
    >
      <div className="relative w-full aspect-[16/9] overflow-hidden" style={{ background: "#0d1117" }}>
        {post.mediaUrl ? (
          <img
            src={post.mediaUrl}
            alt={post.title || "Post image"}
            className="w-full h-full object-cover"
            data-testid={`img-thumb-${post.id}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-white/10" />
          </div>
        )}

        {kwScore > 0 && (
          <div
            className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold backdrop-blur-sm"
            style={{
              background: kwScore >= 60 ? "rgba(34,197,94,0.85)" : "rgba(249,115,22,0.85)",
              color: "#fff",
            }}
            data-testid={`chip-kwscore-${post.id}`}
          >
            KW {kwScore}%
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col flex-1 gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge
            className="text-[10px] font-bold border-none px-1.5 py-0"
            style={{ color: pt.color, background: `${pt.color}18` }}
            data-testid={`badge-type-${post.id}`}
          >
            {pt.icon} {pt.label}
          </Badge>

          <Badge
            className="text-[10px] font-semibold border-none px-1.5 py-0"
            style={{ color: statusPill.color, background: statusPill.bg }}
            data-testid={`badge-status-${post.id}`}
          >
            {statusPill.label}
          </Badge>
        </div>

        {post.title && (
          <h3 className="text-sm font-semibold text-white leading-tight line-clamp-1" data-testid={`text-title-${post.id}`}>
            {post.title}
          </h3>
        )}

        <p className="text-[11px] text-white/40 line-clamp-2 leading-relaxed" data-testid={`text-content-${post.id}`}>
          {post.content?.slice(0, 120) || "Empty draft..."}{(post.content?.length || 0) > 120 ? "…" : ""}
        </p>

        {dateDisplay && (
          <span className="text-[10px] text-white/25 font-mono" data-testid={`text-date-${post.id}`}>
            {dateDisplay}
          </span>
        )}

        <div className="flex-1" />

        <div
          className="flex items-center gap-0.5 pt-2 mt-auto"
          style={{ borderTop: `1px solid ${DD.border}` }}
        >
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2 text-white/40 hover:text-white hover:bg-white/5"
            onClick={e => { e.stopPropagation(); onEdit(post); }}
            data-testid={`button-edit-${post.id}`}
          >
            <Edit2 className="w-3 h-3 mr-1" /> Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2 text-white/40 hover:text-white hover:bg-white/5"
            onClick={e => { e.stopPropagation(); onPreview(post); }}
            data-testid={`button-preview-${post.id}`}
          >
            <Eye className="w-3 h-3 mr-1" /> Preview
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2 text-red-400/60 hover:text-red-400 hover:bg-red-400/5"
            onClick={e => { e.stopPropagation(); onDelete(post); }}
            data-testid={`button-delete-${post.id}`}
          >
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2 text-white/40 hover:text-white hover:bg-white/5"
            onClick={e => { e.stopPropagation(); }}
            data-testid={`button-share-${post.id}`}
          >
            <Share2 className="w-3 h-3 mr-1" /> Share
          </Button>
        </div>
      </div>
    </div>
  );
}
